/**
 * academy-ui/src/template/sections/canvas/CanvasSectionReader.jsx
 *
 * READER-side renderer for a persisted Canva-style Editor section. It is now a THIN
 * ADAPTER over the SAME shared Render_Contract the builder's `ItemView` uses, so the
 * published page reproduces the editor pixel-for-pixel and the two cannot drift
 * (design D1 / Requirement 3.3, 3.4):
 *
 *   - `parseCanvasSection(section)` turns the persisted `section.canvas` block into a
 *     validated `{ schemaVersion, referenceWidth, theme, items, schemaWarning }` model
 *     (malformed items skipped). When the section carries no usable canvas payload the
 *     component renders `null`, so non-canvas sections are completely unaffected and the
 *     caller can fall back (Req 13.3, 13.4, 14.3).
 *   - For each Item it calls the SAME `resolveItemStyle` → `resolveBoxStyle` →
 *     `resolveContent` as the builder, then assembles the box style and maps the
 *     ContentDescriptor to React with the SAME element ordering/nesting as `ItemView`,
 *     minus builder-only chrome (no pointer interaction, no resize handles, no
 *     contentEditable, no selection outline/cursor, no pointerEvents/touchAction, no
 *     selected-only label). (Req 3.3, 3.4, 6.1–6.4, 7.x, 8.x, 15.3, 15.4.)
 *   - Geometry is scaled with the PURE reader primitives `computeScaleFactor` +
 *     `scaleLayout` from `../../layout/layoutCore`, using ONLY the persisted
 *     `referenceWidth`: at a viewport ≥ `referenceWidth` the scale factor is `1.0` and
 *     every box equals its persisted geometry exactly (Req 5.1, 5.2, 5.5, 6.4); when no
 *     viewport is available the effective viewport defaults to `referenceWidth` (scale
 *     factor 1.0 — Req 5.6).
 *   - Items render in array order; `z` is applied as `zIndex`, with ties resolved by
 *     array (document) order (Req 5.3, 5.4). Items carrying a persisted `geometry.hidden`
 *     flag are skipped (Req 4.4). An unknown `type` resolves to an `empty` descriptor, so
 *     the box renders with no inner content (Req 15.3).
 *
 * RESILIENCE: the model has already dropped malformed items, so this component only maps
 * validated records and never throws. It tolerates a missing `template`/`viewportWidth`
 * and an empty / all-hidden item list (Req 15.4).
 *
 * ZERO-LEGACY / ISOLATION: imports ONLY React, the pure reader model in this folder, the
 * synced shared Render_Contract + Theme_Resolver (`canvas-engine/item-contract/`, mirror),
 * and the pure `layoutCore` scaling math. Imports NOTHING from `admin-console-ui`.
 *
 * Requirements: 3.3, 3.4, 4.1, 4.2, 4.3, 4.4, 4.5, 5.1, 5.2, 5.3, 5.4, 5.5, 5.6, 6.4,
 * 15.1, 15.3, 15.4.
 * Design: Components §7 (reader render adapter — mirror of ItemView).
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { computeResponsiveScaleFactor, scaleLayout } from '../../layout/layoutCore';
import { parseCanvasSection } from './canvasReaderModel';
import { resolveItemStyle } from './canvasThemeResolver';
import {
  resolveBoxStyle,
  resolveContent,
} from 'canvas-engine/item-contract/itemRenderContract';
import { elementAnimationStyle } from 'canvas-engine/renderer/elements/elementAnimation';
import { EngineImage } from 'canvas-engine/model/assetUrlResolver';

/** Shared placeholder box used for empty image cells. Copied verbatim from the builder's
 *  `ItemView.ImagePlaceholder` so the empty-image look matches byte-for-byte. */
function ImagePlaceholder({ label }) {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        border: '1px dashed #94A3B8',
        color: '#94A3B8',
        fontSize: 12,
        background: 'rgba(241,245,249,0.6)',
      }}
    >
      {label || 'Image'}
    </div>
  );
}

/** Map an `image` ContentDescriptor (from the contract) to a React node: a real image
 *  when one is present, otherwise the shared placeholder. Mirrors `ItemView.renderImage`. */
function renderImage(props, key) {
  if (props.isPlaceholder) {
    return <ImagePlaceholder key={key} label={props.placeholderLabel} />;
  }
  // Resolve a fresh presigned display URL from the durable stored key at render
  // time (EngineImage + AssetUrlResolverProvider mounted by the reader/preview).
  return (
    <EngineImage
      key={key}
      src={props.src}
      alt={props.alt}
      style={{
        width: '100%',
        height: '100%',
        objectFit: props.objectFit,
        display: 'block',
        borderRadius: props.borderRadius,
      }}
    />
  );
}

/**
 * Map a ContentDescriptor to React, mirroring `ItemView`'s descriptor renderer exactly,
 * minus builder-only chrome. For a `button`, the reader renders a live navigating `<a>`
 * when the Item's `content.href` is present, else a `<button>`, applying the identical
 * visual style object the builder's button uses so the look matches.
 *
 * @param {{ kind: string, props?: object, children?: Array }} descriptor
 * @param {object} item  the validated Item (its `content.href` decides anchor vs button)
 * @returns {JSX.Element|null}
 */
function renderContent(descriptor, item) {
  switch (descriptor.kind) {
    case 'text': {
      const p = descriptor.props;
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            color: p.color,
            fontFamily: p.fontFamily,
            fontSize: p.fontSize,
            fontWeight: p.fontWeight,
            textAlign: p.textAlign,
            lineHeight: p.lineHeight,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {p.text}
        </div>
      );
    }
    case 'image':
      return renderImage(descriptor.props);
    case 'grid': {
      const p = descriptor.props;
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'grid',
            gridTemplateColumns: `repeat(${p.columns}, 1fr)`,
            gap: p.gap,
            boxSizing: 'border-box',
          }}
        >
          {descriptor.children.map((child, idx) => renderImage(child.props, idx))}
        </div>
      );
    }
    case 'button': {
      const p = descriptor.props;
      const buttonStyle = {
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        fontFamily: p.fontFamily,
        fontSize: p.fontSize,
        fontWeight: p.fontWeight,
        borderRadius: p.borderRadius,
        padding: p.padding,
        background: p.background,
        color: p.color,
        border: p.border,
      };
      const href = item.content ? item.content.href : undefined;
      // A present href navigates live; an absent href renders a non-navigating button.
      return href ? (
        <a href={href} style={buttonStyle}>
          {p.label}
        </a>
      ) : (
        <button type="button" style={buttonStyle}>
          {p.label}
        </button>
      );
    }
    case 'empty':
    default:
      // Styled layout boxes (container/paper) and unknown types have no inner content;
      // their appearance comes from the box style only (Req 15.3).
      return null;
  }
}

/** SSR-safe: prefer an explicit prop, else a measured container width, else null. */
function isPositiveFinite(value) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Read the available width of a container element, falling back to `window.innerWidth`.
 * Returns 0 when nothing can be measured (SSR / detached node). Mirrors the reader's
 * existing `readContainerWidth` contract in `layout/useElementLayout`.
 */
function readContainerWidth(el) {
  if (el) {
    if (typeof el.clientWidth === 'number' && el.clientWidth > 0) {
      return el.clientWidth;
    }
    if (typeof el.getBoundingClientRect === 'function') {
      const rect = el.getBoundingClientRect();
      if (rect && Number.isFinite(rect.width) && rect.width > 0) {
        return rect.width;
      }
    }
  }
  if (typeof window !== 'undefined' && Number.isFinite(window.innerWidth)) {
    return window.innerWidth;
  }
  return 0;
}

/** The natural (unscaled) section height = the largest item bottom edge, else 0. */
function referenceHeightOf(items) {
  let maxBottom = 0;
  for (let i = 0; i < items.length; i += 1) {
    const g = items[i].geometry;
    const bottom = g.y + g.height;
    if (bottom > maxBottom) maxBottom = bottom;
  }
  return maxBottom;
}

/**
 * Render a persisted canvas section's items directly from the model.
 *
 * @param {object} props
 * @param {object} props.section - The old-shaped section envelope (carries `canvas`).
 * @param {number} [props.viewportWidth] - Explicit viewport width; when omitted the
 *   component measures its own container and resizes responsively.
 * @param {object} [props.template] - The published template; `template.theme` is the
 *   Template_Theme base of the cascade.
 * @returns {JSX.Element|null} the rendered section, or `null` for a non-canvas section.
 */
export default function CanvasSectionReader({ section, viewportWidth, template }) {
  const model = parseCanvasSection(section);

  const containerRef = useRef(null);
  const [measuredWidth, setMeasuredWidth] = useState(0);

  // Measure the container width for responsive scaling when no explicit width is given.
  useLayoutEffect(() => {
    if (isPositiveFinite(viewportWidth)) return undefined;
    const el = containerRef.current;
    setMeasuredWidth(readContainerWidth(el));
    return undefined;
  }, [viewportWidth]);

  useEffect(() => {
    if (isPositiveFinite(viewportWidth)) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;

    const update = () => setMeasuredWidth(readContainerWidth(el));

    let resizeObserver = null;
    let onWindowResize = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(el);
    } else if (typeof window !== 'undefined') {
      onWindowResize = update;
      window.addEventListener('resize', onWindowResize);
    }
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      if (onWindowResize && typeof window !== 'undefined') {
        window.removeEventListener('resize', onWindowResize);
      }
    };
  }, [viewportWidth]);

  // Non-canvas sections render nothing so the dispatcher can fall back untouched.
  if (model === null) return null;

  const { referenceWidth, theme, items } = model;
  const templateTheme = template && typeof template === 'object' ? template.theme : undefined;
  // Section model passed to the resolver: only its `theme` (Section_Theme) participates.
  const sectionModel = { theme };

  // Resolve the effective viewport: explicit prop → measured container → referenceWidth.
  // Defaulting to referenceWidth yields scaleFactor 1.0 (persisted geometry verbatim,
  // Req 5.6). At a viewport ≥ referenceWidth the scale factor is also 1.0 (Req 5.1).
  let effectiveViewport = referenceWidth;
  if (isPositiveFinite(viewportWidth)) {
    effectiveViewport = viewportWidth;
  } else if (isPositiveFinite(measuredWidth)) {
    effectiveViewport = measuredWidth;
  }

  const scaleFactor = computeResponsiveScaleFactor(effectiveViewport, referenceWidth);
  const scaledWidth = referenceWidth * scaleFactor;
  const scaledHeight = referenceHeightOf(items) * scaleFactor;

  return (
    <section
      ref={containerRef}
      data-canvas-reader-section="true"
      style={{ position: 'relative', width: '100%', overflowX: 'hidden' }}
    >
      <div
        data-canvas-reader-stage="true"
        style={{
          position: 'relative',
          width: scaledWidth,
          height: scaledHeight,
          margin: '0 auto',
          backgroundImage:
            typeof theme.backgroundImage === 'string' && theme.backgroundImage.trim()
              ? `url(${theme.backgroundImage.trim()})`
              : undefined,
          backgroundSize:
            typeof theme.backgroundImage === 'string' && theme.backgroundImage.trim()
              ? 'cover'
              : undefined,
          backgroundPosition:
            typeof theme.backgroundImage === 'string' && theme.backgroundImage.trim()
              ? 'center'
              : undefined,
          backgroundRepeat:
            typeof theme.backgroundImage === 'string' && theme.backgroundImage.trim()
              ? 'no-repeat'
              : undefined,
        }}
      >
        {items.map((item) => {
          // Skip items the author hid (validateElementLayout passes `hidden` through) so
          // hidden items render no element on the published page (Req 4.4).
          if (item.geometry && item.geometry.hidden === true) return null;

          // ── Render_Contract: the SAME calls the builder's ItemView makes ──────────
          const resolved = resolveItemStyle(item, sectionModel, templateTheme);
          const box = resolveBoxStyle(item, resolved);
          const descriptor = resolveContent(item, resolved);

          // Scaled geometry from the persisted referenceWidth (Req 5.2, 5.5, 14.2).
          const geo = scaleLayout(item.geometry, scaleFactor);
          const isPaper = item.type === 'paper';
          const isContainer = item.type === 'container';

          // Per-element entrance animation (additive, mirrors ItemView).
          const animStyle = elementAnimationStyle(item.animation) || {};

          // Box style mirrors ItemView's `boxStyle` EXACTLY (same undefined-collapsing),
          // minus builder-only chrome (outline/cursor/touchAction/pointerEvents), and
          // using the SCALED geometry for transform/width/height/zIndex.
          const boxStyle = {
            position: 'absolute',
            top: 0,
            left: 0,
            transform: `translate(${geo.x}px, ${geo.y}px)`,
            width: geo.width,
            height: geo.height,
            zIndex: geo.z,
            boxSizing: 'border-box',
            backgroundColor: box.backgroundColor,
            backgroundImage: box.backgroundImage ? `url(${box.backgroundImage})` : undefined,
            backgroundSize: box.backgroundImage ? 'cover' : undefined,
            backgroundPosition: box.backgroundImage ? 'center' : undefined,
            backgroundRepeat: box.backgroundImage ? 'no-repeat' : undefined,
            opacity: box.opacity,
            borderRadius: box.borderRadius || undefined,
            border: box.borderWidth ? `${box.borderWidth}px solid ${box.borderColor}` : undefined,
            boxShadow: box.boxShadow !== 'none' ? box.boxShadow : undefined,
            padding: (isPaper || isContainer) && box.padding ? box.padding : undefined,
            // Merge entrance animation styles (animationName, duration, delay, etc.)
            ...animStyle,
          };

          return (
            <div key={item.id} data-canvas-reader-item={item.id} style={boxStyle}>
              {renderContent(descriptor, item)}
            </div>
          );
        })}
      </div>
    </section>
  );
}
